/**
 * FOR SALE — fiche d'une annonce (/pages/listings/<handle>) : entrée de l'équipe (demande de Charles du 12 octobre 2026,
 * qui remplace « rien pour l'équipe sur la fiche » du 9 octobre pour la modification).
 *
 * Pastille « Équipe » (piste A, refonte du 15 octobre 2026) : posée dans la barre de la fiche (à côté de la référence et de
 * « Partager »), montrée seulement à un compte de l'équipe connecté (rôle lu sur /me, relu par le service à chaque appel).
 * La fiche publique ne bouge plus d'un pixel ; l'emplacement [data-fs-listing-admin] (rendu masqué par
 * fs-listing-page.liquid) ne sert plus que de repli quand la barre manque (annonce retirée : page « plus disponible »). Son
 * chiffre compte les points « À traiter » (GET /admin/listings/:handle et …/content, lus en fond). Elle ouvre le tiroir de
 * l'équipe (fs-listing-drawer.js, chargé à la demande ; refonte du 29 octobre 2026) : identité de l'annonce lue sur la fiche,
 * prix et état, gestes, à traiter, tickets, sortie de la vente. Le tiroir se rouvre après un enregistrement qui recharge la
 * fiche ; un prix en cours de propagation est suivi sur place, tiroir ouvert.
 *  - « Modifier l'annonce » (action principale du tiroir) ouvre une fenêtre modale qui reprend le parcours de création (fs-propose.js, mode équipe),
 *    prérempli avec l'annonce en ligne (GET /admin/listings/:handle/edit) : mêmes étapes, champs et captures, puis
 *    « Vérifier » avec ce qui change et un motif obligatoire. Administrateur (publication) : contenu et captures appliqués à
 *    l'enregistrement, prix par la route prix existante (commission ou net changé : appliqué aussitôt, sans accord du
 *    vendeur, 025-net-fixe-par-equipe) ; autre membre de l'équipe (MiddleMan, relecteur) : proposition relue dans « À relire », sans
 *    prix (jamais de net ni de commission pour lui, décision 018).
 *  - annonce venue de Discord (administrateur, demande de Charles du 13 octobre 2026) : le même parcours, en « reprise ».
 *    La première fois, il est prérempli avec ce que le post du vendeur permet de lire ; ensuite, avec le formulaire
 *    enregistré. La fiche suit ce formulaire, le post Discord du vendeur ne change pas, les captures ne sont pas reprises ;
 *  - prix, retrait de la vente, remise en vente, refus d'une demande de suppression du vendeur, corbeille : actions de
 *    l'espace équipe ramenées dans le tiroir (administrateurs), mêmes routes et même rejeu sans doublon ;
 *  - « Espace équipe » (pied du tiroir) ouvre la fiche de travail de l'annonce ;
 *  - administrateur : section « Tickets » (fs-listing-people.js), vendeur et acheteurs avec leurs tickets Discord.
 * Rien pour un visiteur : aucun élément montré sans session de l'équipe. Aucun montant calculé ici : le service calcule
 * (aperçus POST …/price/preview et /admin/pricing/quote, pendant la saisie dans le tiroir : priceEditor) ; ce module lit les
 * saisies et formate (fs-admin-price.js). Textes en français
 * (espace équipe, décision INTL). Fonctions pures exportées pour les tests Node.
 */
import { el as h, put, add, alert, toast, skeleton, segmented, segment, chip } from './fs-ui.js';
import { dialog, confirmDialog } from './fs-ui-dialog.js';
import { path, number } from './fs-i18n.js';

const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
/** Compte Discord (sub) d'un jeton de session, lu sans vérification : clé d'interface seulement, jamais un droit. */
export function sessionSub(token) {
  try {
    const part = String(token ?? '').split('.')[1] ?? '';
    const sub = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='))).sub;
    return typeof sub === 'string' ? sub.slice(0, 80) : '';
  } catch { return ''; }
}
const GAME = /^[a-z][a-z0-9-]{1,39}$/;

/**
 * Droits de la barre de l'équipe pour un rendu : /me lu, puis la feuille de l'équipe chargée (équipe seulement). `current()`
 * est revérifié après chaque attente : un rendu dépassé par un changement de session rend { stale: true } sans droits.
 */
export async function barRights({ me, css, current }) {
  const rights = (await me()) ?? null;
  if (!current()) return { stale: true, rights: null };
  if (rights?.team) {
    await css();
    if (!current()) return { stale: true, rights: null };
  }
  return { stale: false, rights };
}
const FLASH_KEY = 'fs-listing-admin-flash';

/*
 * Textes du parcours de création en mode équipe (fs-propose.js, option `admin`) : français dans toutes les langues de la
 * page (décision INTL : ce que seule l'équipe voit reste en français), fournis par cette fiche.
 */
export const ADMIN_TEXT = Object.freeze({
  saveButton: 'Enregistrer les modifications',
  priceUnavailable: 'Prix indisponible ici : modifiez-le depuis l’espace équipe.',
  priceTitle: 'Prix',
  priceLead: 'Net vendeur et commission : le service calcule le prix affiché. Le nouveau prix s’applique à l’enregistrement, sans accord du vendeur.',
  proposeButton: 'Proposer la modification',
  directTitle: 'Modification par l’équipe',
  takeoverNewTitle: 'Reprise d’une annonce venue de Discord',
  takeoverNewText: 'Le formulaire est prérempli avec ce que le post du vendeur permet de lire : complétez le reste, vaisseaux compris, puis enregistrez depuis « Vérifier ». La fiche suivra ensuite ce formulaire ; le post Discord du vendeur ne change pas. Les captures ne sont pas reprises.',
  takeoverTitle: 'Annonce reprise dans le formulaire',
  takeoverText: 'Tout est prérempli avec le formulaire enregistré : changez ce qu’il faut, puis enregistrez depuis « Vérifier ». Le post Discord du vendeur ne change pas.',
  reviewTitle: 'Proposition de l’équipe',
  directText: 'Tout est prérempli avec l’annonce en ligne : changez ce qu’il faut, puis enregistrez depuis « Vérifier ». Les changements s’appliquent dès l’enregistrement.',
  reviewText: 'Tout est prérempli avec l’annonce en ligne : changez ce qu’il faut, puis proposez la modification depuis « Vérifier ». Un administrateur la relit ; l’annonce en ligne ne change pas d’ici là.',
  changesTitle: 'Ce qui change',
  noChange: 'Rien n’a changé pour le moment.',
  takeoverChange: 'Toute l’annonce passe au formulaire du site (titre, étiquettes, caractéristiques, points clés).',
  unchanged: 'Rien n’a changé : modifiez au moins un élément, ou fermez la fenêtre.',
  reasonLabel: 'Motif',
  reasonPlaceholder: 'Ex. : vaisseau ajouté d’après la capture du vendeur',
  reasonHintDirect: '3 à 300 caractères. Gardé dans l’historique privé de l’annonce, jamais montré au vendeur.',
  reasonHintReview: '3 à 300 caractères. Lu par l’administrateur qui relit votre proposition.',
  reasonError: 'motif obligatoire : 3 à 300 caractères',
  saving: 'Enregistrement…',
  proposing: 'Envoi de la proposition…',
  uploading: (index, total) => `Envoi des captures (${index} sur ${total})…`,
  failedShots: (count) => `${count > 1 ? `${count} captures n’ont` : 'Une capture n’a'} pas pu être envoyée${count > 1 ? 's' : ''} : réessayez ou retirez-la${count > 1 ? 's' : ''}.`,
  noSession: 'Session expirée : reconnectez-vous depuis l’en-tête du site, puis rouvrez la fenêtre.',
  tooLarge: 'Annonce trop longue pour être envoyée : raccourcissez les textes.',
});
/** Noms des champs propres à la modification par l'équipe (résumé d'erreurs, « Ce qui change »). */
export const ADMIN_LABELS = Object.freeze({ adminReason: 'Motif', adminNet: 'Net vendeur', adminRate: 'Commission' });
const ADMIN_ERRORS = Object.freeze({
  version_changed: 'L’annonce a changé depuis l’ouverture de cette fenêtre. Fermez-la et rouvrez-la pour repartir de la version en ligne.',
  amendment_pending: 'Une autre modification de cette annonce attend sa relecture : traitez-la d’abord dans « À relire ».',
  listing_sold: 'Annonce vendue : elle ne se modifie plus.',
  not_listed: 'Annonce retirée ou masquée : remettez-la en vente avant de la modifier.',
  listing_has_active_deal: 'Une vente est en cours (lien, commande ou paiement) : l’annonce ne change pas pendant ce temps.',
  listing_control_busy: 'Une autre opération est en cours sur cette annonce. Réessayez dans un instant.',
  busy: 'Une autre opération est en cours sur cette annonce. Réessayez dans un instant.',
  edit_unavailable: 'Modification indisponible pour le moment : le service n’a pas encore les champs nécessaires.',
  content_unchanged: ADMIN_TEXT.unchanged,
  reason_required: 'Motif obligatoire : 3 à 300 caractères.',
  operation_uncertain: 'Réponse incertaine : rien n’est confirmé. « Enregistrer » relance la même demande, sans doublon.',
  request_id_conflict: 'Une demande différente porte déjà cet identifiant : fermez la fenêtre et rouvrez-la.',
  forbidden: 'Votre rôle ne permet pas cette modification. Reconnectez-vous si vos rôles ont changé.',
  discord_listing: 'Annonce venue de Discord : son contenu se corrige dans l’espace équipe.',
  not_site_listing: 'Cette annonce ne vient pas du formulaire du site.',
  listing_not_found: 'Annonce introuvable.',
  invalid: 'Certains champs sont refusés par le service : corrigez-les, puis enregistrez.',
  media_unavailable: 'Les captures sont indisponibles pour le moment : réessayez dans un instant.',
  rate_limited: 'Trop de demandes d’affilée : patientez une minute.',
  takeover_too_large: 'Annonce trop détaillée pour la fiche (80 caractéristiques au plus) : allégez les textes libres.',
  listing_control_unavailable: 'Modification indisponible pour le moment (service).',
  publisher_required: 'Réservé aux administrateurs.',
});
/** Refus de POST /admin/listings/:handle/edit → phrase pour l'équipe. */
export function adminErrorText(status, body) {
  const code = typeof body?.error === 'string' ? body.error : '';
  if (ADMIN_ERRORS[code]) return ADMIN_ERRORS[code];
  if (status === 401) return ADMIN_TEXT.noSession;
  if (status === 403) return ADMIN_ERRORS.forbidden;
  if (status === 429) return ADMIN_ERRORS.rate_limited;
  if (status === 409) return ADMIN_ERRORS.version_changed;
  return 'Le service ne répond pas : rien n’est confirmé. Réessayez dans un instant.';
}
/** Motif de l'équipe : '' s'il est recevable (3 à 300 caractères, comme le service), sinon le message. */
export const adminReasonError = (text) => { const length = [...String(text ?? '').trim()].length; return length < 3 || length > 300 ? ADMIN_TEXT.reasonError : ''; };

/** Annonce non modifiable (raison de GET …/edit) → phrase pour l'équipe. */
export const BLOCKED = Object.freeze({
  listing_sold: 'Annonce vendue : elle ne se modifie plus.',
  not_listed: 'Annonce retirée ou masquée : remettez-la en vente depuis l’espace équipe avant de la modifier.',
  amendment_pending: 'Une modification de cette annonce attend sa relecture : traitez-la d’abord dans « À relire ».',
  listing_has_active_deal: 'Une vente est en cours (lien de paiement, commande ou paiement) : l’annonce ne change pas pendant ce temps.',
  listing_control_busy: 'Une autre opération est en cours sur cette annonce. Réessayez dans un instant.',
  listing_control_unavailable: 'Modification indisponible pour le moment (service).',
  edit_unavailable: 'Modification indisponible pour le moment : le service n’a pas encore les champs nécessaires.',
  not_site_listing: 'Cette annonce ne vient ni du formulaire du site ni de Discord : elle ne se modifie pas ici.',
  publisher_required: 'Annonce venue de Discord : sa reprise dans le formulaire est réservée aux administrateurs.',
  discord_listing: 'Annonce venue de Discord : son contenu se corrige dans l’espace équipe.',
});
/** Préremplissage d'une reprise neuve (`prefill` du service) → phrase du bandeau : champs lus dans le post du vendeur. */
export function takeoverNote(prefill) {
  const count = Number.isSafeInteger(prefill?.recognized) && prefill.recognized > 0 ? prefill.recognized : 0;
  const head = count ? `${count} champ${count > 1 ? 's' : ''} repris du post du vendeur : vérifiez-les.` : 'Rien n’a pu être lu automatiquement dans le post du vendeur : remplissez le formulaire.';
  // Avertissements de l'analyseur (valeur hors limites, choix inconnu, vaisseaux non reconnus) : 3 au plus, texte brut.
  const notes = (Array.isArray(prefill?.warnings) ? prefill.warnings : []).filter((text) => typeof text === 'string' && text.trim()).slice(0, 3)
    .map((text) => text.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 160));
  return notes.length ? `${head} À vérifier : ${notes.join(' ')}` : head;
}
/** Lecture de GET /admin/listings/:handle/edit refusée → phrase. */
export function loadErrorText(status, body) {
  if (status === 401) return 'Session expirée : reconnectez-vous depuis l’en-tête du site.';
  if (status === 403) return 'Votre compte n’a pas (ou plus) de rôle d’équipe.';
  if (status === 404) return 'Annonce introuvable.';
  if (status === 429) return 'Trop de demandes d’affilée : patientez une minute.';
  if (BLOCKED[body?.error]) return BLOCKED[body.error];
  return 'Le service ne répond pas : réessayez dans un instant.';
}
/**
 * Réponse de GET …/edit → ce que la fenêtre montre : 'site' (parcours de création), 'discord' (contenu public), ou
 * 'blocked' avec sa phrase. `mode` : 'direct' (appliqué) ou 'review' (proposé).
 */
export function editView(body) {
  const mode = body?.mode === 'direct' ? 'direct' : 'review';
  if (!body || typeof body !== 'object') return { kind: 'blocked', mode, text: loadErrorText(0, null) };
  if (body.editable !== true) return { kind: 'blocked', mode, text: BLOCKED[body.reason] ?? 'Cette annonce ne se modifie pas ici pour le moment.' };
  if (body.source === 'site' && GAME.test(body.game ?? '')) return { kind: 'site', mode };
  /* Annonce venue de Discord (administrateur) : le même parcours de création, en reprise (première fois ou suivante). */
  if (body.source === 'discord' && mode === 'direct' && ['new', 'resumed'].includes(body.takeover) && GAME.test(body.game ?? '')) {
    return { kind: 'site', mode, takeover: body.takeover };
  }
  return { kind: 'blocked', mode, text: BLOCKED[body.source === 'discord' ? 'publisher_required' : 'not_site_listing'] };
}

/**
 * Issue d'un enregistrement → { text, tone, reload }. `content` : réponse de …/edit ou …/content (ou null si seul le
 * prix change) ; `price` : { ok, status, message } du bloc du prix, ou null.
 */
export function savedMessage({ content = null, price = null } = {}) {
  const parts = [];
  let reload = false;
  let tone = 'success';
  if (content) {
    const status = content.status;
    if (status === 'pending') parts.push('Proposition envoyée : un administrateur la relit dans « À relire ». L’annonce en ligne ne change pas d’ici là.');
    else if (status === 'rejected') { parts.push('Cette demande a déjà été traitée : la modification a été refusée ou n’a pas pu s’appliquer. Rien n’a changé.'); tone = 'warning'; }
    else if (status === 'approved') { parts.push('Modification enregistrée : elle s’applique dans quelques minutes.'); tone = 'info'; }
    else if (content.replayed) { parts.push('Cette demande avait déjà été enregistrée. La fiche est actualisée.'); reload = true; tone = 'info'; }
    else if (content.takeover === 'new') { parts.push('Annonce reprise dans le formulaire : la fiche suit maintenant ce formulaire. Le post Discord du vendeur ne change pas.'); reload = true; }
    else { parts.push('Annonce modifiée.'); reload = true; }
  }
  if (price) {
    if (!price.ok) { parts.push(`Prix non modifié : ${price.message || 'le service a refusé le changement.'}`); tone = 'warning'; }
    // Ancienne instance pendant un redéploiement : proposition au vendeur, prix inchangé (jamais « appliqué »).
    else if (price.status === 'pending_seller') {
      parts.push('Prix non modifié : le service, en cours de mise à jour, a enregistré une proposition au vendeur. Dans quelques minutes, réappliquez ce prix.');
      tone = 'warning';
    } else if (price.status === 'kept') { parts.push(`Prix gardé, attente levée${price.shown ? ` : ${price.shown}` : ''}.`); reload = true; }
    // Prix enregistré, fiche, offre et Discord suivis sur place (…/price/status) : pas de rechargement de la fiche.
    else if (price.propagation?.status === 'pending') { parts.push(`Nouveau prix appliqué${price.shown ? ` : ${price.shown}` : ''}. ${PROPAGATING_TEXT}`); if (!content) tone = 'info'; }
    else if (price.propagation?.status === 'failed') { parts.push(`Nouveau prix enregistré${price.shown ? ` : ${price.shown}` : ''}, ${PROPAGATION_FAILED}`); tone = 'warning'; }
    else { parts.push(`Nouveau prix appliqué${price.shown ? ` : ${price.shown}` : ''}.`); reload = true; }
  }
  return { text: parts.join(' '), tone, reload };
}

/* ------------------------------------------------------------------ prix (administrateurs) */

const rid = (prefix) => `${prefix}-${globalThis.crypto.randomUUID()}`;
const PROPAGATING_TEXT = 'Discord et offre en cours de mise à jour…';
const PROPAGATION_FAILED = 'mais la fiche, l’offre d’achat ou le sujet Discord ne sont pas confirmés. L’équipe est alertée ; l’annonce reste réservée jusqu’à vérification.';
/* Le service répond dès que le registre a accepté le prix (2 à 3 s) ; au-delà de ce délai, l'état de la demande est relu
   (…/price/status) plutôt que renvoyé à l'aveugle. */
export const PRICE_WRITE_MS = 30000;
/* Lectures de …/price/status : attentes croissantes (1, 2, 3, 5 s, puis 8 s), 90 s au plus, soit une quinzaine de lectures,
   loin de la limite du service (60 requêtes par minute et par IP, partagée par l'équipe derrière une même IP). */
export const STATUS_STEPS = Object.freeze([1000, 2000, 3000, 5000]);
export const STATUS_MAX_MS = 90000;
/**
 * Attente avant la lecture suivante (ms) après `attempt` lectures ; refus pour débit (429) : plus longue, d'après
 * Retry-After (au moins 10 s, au plus 30 s), jamais un abandon.
 */
export function statusWait(attempt, response = null) {
  const base = STATUS_STEPS[attempt] ?? 8000;
  if (response?.status !== 429) return base;
  const retry = Number(response.headers?.get?.('retry-after'));
  return Math.min(30000, Math.max(base * 2, 10000, Number.isFinite(retry) && retry > 0 ? retry * 1000 : 0));
}
/**
 * État d'une demande de prix relu jusqu'à `settled(body)` ou la fin du budget → { body, read, reads } : dernier état lu
 * (ou null), `read` faux si la dernière lecture n'a pas abouti (service injoignable, 429), nombre de lectures ; null si
 * `current()` devient faux (session changée). `waitFirst` : attendre avant la première lecture.
 */
export async function readStatus(call, path, { budgetMs = STATUS_MAX_MS, waitFirst = true, settled = (body) => !['writing', 'pending'].includes(body?.status),
  current = () => true, sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }), now = () => Date.now() } = {}) {
  const until = now() + budgetMs;
  let body = null, read = false, reads = 0, wait = waitFirst ? statusWait(0) : 0;
  for (;;) {
    if (wait) { const left = until - now(); if (left <= 0) break; await sleep(Math.min(wait, left)); }
    if (!current()) return null;
    let response;
    try { response = await call(path); } catch { response = { status: 0, body: null }; }
    reads++;
    read = response.status === 200 && Boolean(response.body);
    if (read) { body = response.body; if (settled(body)) break; }
    wait = statusWait(reads, response);
    if (now() >= until) break;
  }
  return { body, read, reads };
}
/**
 * Réponse de POST …/price (ou état relu par …/price/status) → { ok, status, shown, propagation, priceMinor } du bloc du
 * prix, ou null si rien n'est sûr (réponse perdue, demande inconnue : la même demande peut être rejouée sans doublon).
 * `status` du suivi : pending | done | failed (prix enregistré) ; refused (rien d'écrit) ; writing, incomplete, unconfirmed,
 * not_found : rien de sûr (null).
 */
export function priceResult(lib, response, { kind = 'net', requestId = null } = {}) {
  const body = response?.body;
  const shown = (minor) => (Number.isSafeInteger(minor) ? lib.formatPrice(minor, body?.price?.currency) : '');
  if (response?.status === 200 && body && typeof body.status === 'string' && 'requestId' in body) {
    if (['pending', 'done', 'failed'].includes(body.status)) {
      const minor = body.price?.minor;
      return { ok: true, status: kind === 'keep' ? 'kept' : 'applied', priceMinor: Number.isSafeInteger(minor) ? minor : null,
        shown: `${shown(minor)}${body.status === 'done' ? lib.surfacesText(body.surfaces) : ''}`,
        propagation: { status: body.status, requestId: body.requestId } };
    }
    if (body.status === 'refused' && body.error?.code) return { ok: false, status: body.error.status ?? 409, message: lib.priceErrorText(body.error.status ?? 409, { error: body.error.code }) };
    return null;
  }
  if (response?.status !== 200) return null;
  const minor = body?.price?.minor;
  // Appliqué (status 'applied', 025-net-fixe-par-equipe) ; une ancienne instance pendant un redéploiement peut encore
  // rendre une proposition au vendeur : jamais annoncée comme appliquée.
  const status = body?.status !== 'applied' ? 'pending_seller' : kind === 'keep' ? 'kept' : 'applied';
  const propagation = body?.propagation?.status === 'pending' ? { status: 'pending', requestId: body.propagation.requestId ?? requestId } : null;
  return { ok: true, status, priceMinor: Number.isSafeInteger(minor) ? minor : null, shown: shown(minor) ? `${shown(minor)}${lib.surfacesText(body?.surfaces)}` : '', propagation };
}
/** Champ du bloc du prix, même balisage que les champs du parcours (data-fs-field, message d'erreur, aide). */
function priceField(key, label, input, unit, hint) {
  const id = input.id;
  return h('div', { class: 'fs-field fs-num fs-propose-field fs-w-tiers fs-size-m', 'data-fs-field': key },
    h('p', { class: 'fs-field-head' }, h('label', { class: 'fs-field-label fs-propose-label', for: id }, h('span', { class: 'fs-propose-label-text' }, h('span', { class: 'fs-propose-label-name', text: label })))),
    h('div', { class: 'fs-field-ctl' }, h('div', { class: 'fs-num-box has-unit' }, input, h('span', { class: 'fs-num-unit', 'aria-hidden': 'true', text: unit }))),
    h('div', { class: 'fs-field-msg' }, h('p', { class: 'fs-field-error fs-propose-error', id: `${id}-error`, 'data-fs-error': true, hidden: true }),
      hint ? h('p', { class: 'fs-field-hint fs-propose-hint', id: `${id}-hint`, text: hint }) : null));
}

/**
 * Bloc « Prix » (publication) d'après GET /admin/listings/:handle (`listing.price`, `listing.pricing`) : prix affiché, net,
 * commission, proposition laissée en attente par l'ancienne règle, verrou ; net et commission modifiables, aperçu du service. Rend null s'il n'y a
 * rien à modifier ici (prix éteints). Sans dossier vendeur, le net saisi fixe le premier prix (025-net-fixe-par-equipe).
 * { node, errors(), showErrors(errors), change(), summary(), save({ reason }) } ; `lib` : fs-admin-price.js.
 */
export function priceBlock(lib, current, { handle, call }) {
  const view = lib.priceView(current, { publisher: true });
  const pricing = current?.pricing;
  if (!view.action || !pricing) return null;
  const base = Number.isSafeInteger(pricing.netMinor) && Number.isSafeInteger(pricing.rateBps) ? { netMinor: pricing.netMinor, rateBps: pricing.rateBps } : null;
  const locked = Boolean(view.lock);
  const shownPrice = Number.isSafeInteger(current?.price?.minor) && current.price.source === 'listing' ? current.price.minor : null;
  // Façon de changer le prix (028-baisse-prix-proportionnelle) : « Baisser de … % » et « Nouveau prix » gardent le taux, le
  // service répartit au prorata entre le vendeur et la commission ; « Net / commission » reste (025). Sans accord : net seul.
  let mode = base ? 'percent' : 'net';
  const percent = h('input', { class: 'fs-input', id: 'fs-listing-admin-percent', type: 'text', inputmode: 'decimal', autocomplete: 'off', placeholder: '20', disabled: locked,
    'aria-describedby': 'fs-listing-admin-percent-hint', 'data-fs-listing-admin-percent': true });
  const target = h('input', { class: 'fs-input', id: 'fs-listing-admin-target', type: 'text', inputmode: 'numeric', autocomplete: 'off', placeholder: shownPrice ? lib.netInput(shownPrice) : '', disabled: locked,
    'aria-describedby': 'fs-listing-admin-target-hint', 'data-fs-listing-admin-target': true });
  // Net au centime en vigueur (prix de l'admin Shopify) : prérempli tel quel ; laissé tel quel, il n'est ni relu ni envoyé.
  const net = h('input', { class: 'fs-input', id: 'fs-listing-admin-net', type: 'text', inputmode: 'decimal', autocomplete: 'off', value: lib.netField(pricing.netMinor), disabled: locked,
    'aria-describedby': 'fs-listing-admin-net-hint', 'data-fs-listing-admin-net': true });
  const rate = h('input', { class: 'fs-input', id: 'fs-listing-admin-rate', type: 'text', inputmode: 'decimal', autocomplete: 'off', value: lib.rateInput(pricing.rateBps ?? pricing.defaultRateBps ?? 2000),
    disabled: locked, 'aria-describedby': 'fs-listing-admin-rate-hint', 'data-fs-listing-admin-rate': true });
  const hide = base ? h('input', { type: 'checkbox', id: 'fs-listing-admin-hide', disabled: locked, 'data-fs-listing-admin-hide': true }) : null;
  const recap = h('div', { class: 'fs-listing-admin-recap', 'aria-live': 'polite', 'data-fs-listing-admin-recap': true });
  // Révision restée en attente du vendeur (ancienne règle) : case explicite pour garder l'accord en vigueur et lever l'attente.
  // Cochée, aucune saisie n'est lue ni envoyée (net au centime compris) : le service réapplique l'accord tel quel.
  const kept = locked ? null : lib.keepRequest(pricing);
  const keep = kept ? h('input', { type: 'checkbox', id: 'fs-listing-admin-keep', 'data-fs-listing-admin-keep': true }) : null;
  let previewed = null; // { signature, lines } : aperçu du service pour la saisie en cours
  const inputs = () => ({ reductionBps: lib.parseReduction(percent.value), targetPriceMinor: lib.parseTargetPrice(target.value),
    netMinor: lib.readNet(net.value, base?.netMinor), rateBps: lib.parseRate(rate.value) });
  const signature = () => JSON.stringify([mode, percent.value.trim(), target.value.trim(), net.value.trim(), rate.value.trim(), Boolean(hide?.checked)]);
  const empty = () => (mode === 'percent' ? !percent.value.trim() : mode === 'target' ? !target.value.trim() : !base && !net.value.trim());
  // Rien saisi (pourcentage ou nouveau prix vide, premier prix sans net) : corriger la fiche n'oblige pas à changer le prix.
  const untouched = () => empty() && !hide?.checked;
  const errors = () => {
    if (locked || keep?.checked || untouched()) return {};
    const value = inputs();
    if (mode === 'percent') return value.reductionBps === null ? { adminPercent: 'un pourcentage de 0,01 à 99, avec au plus deux décimales' } : {};
    if (mode === 'target') return value.targetPriceMinor === null ? { adminTarget: 'un nombre entier d’euros' } : {};
    return { ...(value.netMinor === null ? { adminNet: 'un nombre entier d’euros, de 1 à 10 000' } : {}),
      ...(value.rateBps === null ? { adminRate: 'un pourcentage de 0 à 100, avec au plus deux décimales' } : {}) };
  };
  const LABELS = { percent: 'Prix (baisse en %)', target: 'Prix (nouveau prix)', net: 'Prix (net vendeur)', rate: 'Prix (commission)' };
  const change = () => {
    if (locked) return null;
    if (keep?.checked) return { kind: 'keep', body: kept, label: 'Prix (gardé, attente levée)' };
    if (untouched()) return { kind: 'none', body: {} };
    if (Object.keys(errors()).length) return null;
    // Case seule (pourcentage et nouveau prix vides) : « Ne pas afficher comme une baisse » sans changer le prix, impossible ici.
    if (empty()) return { kind: 'none', body: {} };
    const result = lib.priceRequest(mode, inputs(), base, { hide: Boolean(hide?.checked) });
    return result.kind === 'none' || result.kind === 'invalid' ? result : { ...result, label: LABELS[result.kind] };
  };
  const quote = h('button', { type: 'button', class: 'fs-btn fs-btn--sm', 'data-fs-listing-admin-quote': true, disabled: locked, text: base ? 'Calculer l’aperçu' : 'Calculer le prix affiché' });
  quote.addEventListener('click', async () => {
    const wanted = change();
    if (!wanted || wanted.kind === 'none' || wanted.kind === 'invalid' || wanted.kind === 'keep') {
      put(recap, h('p', { class: 'fs-field-hint', text: wanted?.kind === 'keep' ? 'Prix gardé tel quel : rien à calculer.' : 'Saisissez une baisse, un nouveau prix, ou un net et une commission lisibles.' }));
      return;
    }
    put(recap, h('p', { class: 'fs-field-hint', text: 'Calcul par le service…' }));
    const seen = signature();
    let response;
    const value = inputs();
    try {
      response = base ? await call(`/admin/listings/${encodeURIComponent(handle)}/price/preview`, { method: 'POST', body: wanted.body })
        : await call('/admin/pricing/quote', { method: 'POST', body: { netMinor: value.netMinor, rateBps: value.rateBps } });
    } catch { response = { status: 0, body: null }; }
    if (seen !== signature()) return;
    if (!base) {
      if (response.status !== 200 || !Number.isSafeInteger(response.body?.buyerTotalMinor)) { put(recap, h('p', { class: 'fs-field-hint', text: lib.priceErrorText(response.status, response.body) })); return; }
      put(recap, h('p', { class: 'fs-listing-admin-quote-line' }, 'Prix affiché : ', h('strong', { text: lib.formatPrice(response.body.buyerTotalMinor) }),
        ` · commission ${lib.formatPrice(response.body.retainedMinor)} (${lib.formatRate(value.rateBps)})`));
      return;
    }
    const lines = response.status === 200 ? lib.previewLines(response.body) : null;
    if (!lines) { put(recap, h('p', { class: 'fs-field-hint', text: lib.priceErrorText(response.status, response.body) })); return; }
    previewed = { signature: seen, lines };
    put(recap, h('dl', { class: 'fs-listing-admin-facts', 'data-fs-listing-admin-preview': true },
      h('div', {}, h('dt', { text: 'Prix affiché' }), h('dd', {}, h('strong', { text: lines.price }))),
      h('div', {}, h('dt', { text: 'Net vendeur' }), h('dd', { text: lines.seller })),
      h('div', {}, h('dt', { text: 'Commission' }), h('dd', { text: lines.commission })),
      h('div', {}, h('dt', { text: 'Sur le site' }), h('dd', { 'data-fs-listing-admin-badge': true, text: lines.badge }))),
    lines.netChanged ? h('p', { class: 'fs-field-hint', text: 'Le net du vendeur change sans son accord : prévenez-le dans son ticket si l’avis automatique n’est pas allumé.' }) : null);
  });
  [percent, target, net, rate].forEach((input) => input.addEventListener('input', () => { previewed = null; put(recap); }));
  hide?.addEventListener('change', () => { previewed = null; put(recap); });
  const percentRow = h('div', { class: 'fs-form-grid', 'data-fs-listing-admin-mode-row': 'percent' },
    priceField('adminPercent', 'Baisser de', percent, '%', 'Le prix affiché baisse de ce pourcentage, arrondi à l’euro inférieur ; le net du vendeur et la commission baissent dans la même proportion.'));
  const targetRow = h('div', { class: 'fs-form-grid', 'data-fs-listing-admin-mode-row': 'target' },
    priceField('adminTarget', 'Nouveau prix affiché', target, '€', `${shownPrice ? `Actuellement ${lib.formatPrice(shownPrice)}. ` : ''}Le net du vendeur et la commission suivent au prorata, commission au même taux.`));
  const netRow = h('div', { class: 'fs-form-grid', 'data-fs-listing-admin-mode-row': 'net' },
    priceField('adminNet', 'Net vendeur', net, '€', base ? `Actuellement ${lib.formatPrice(base.netMinor)}. Laissé tel quel, il ne change pas. Un net changé s’applique sans accord du vendeur.` : 'Montant que le vendeur recevra.'),
    priceField('adminRate', 'Commission', rate, '%', `${base ? 'Actuellement' : 'Par défaut'} ${lib.formatRate(base?.rateBps ?? pricing.defaultRateBps ?? 2000)}. Commission seule : appliquée avec votre motif.`));
  const rows = { percent: percentRow, target: targetRow, net: netRow };
  const MODES = [['percent', 'Baisser de … %'], ['target', 'Nouveau prix'], ['net', 'Net / commission']];
  const switcher = base ? h('div', { class: 'fs-seg fs-seg--fit', role: 'group', 'aria-label': 'Façon de changer le prix', 'data-fs-listing-admin-modes': true },
    ...MODES.map(([id, label]) => {
      const button = h('button', { type: 'button', class: 'fs-seg-btn', 'aria-pressed': String(id === mode), disabled: locked, 'data-fs-listing-admin-mode': id, text: label });
      button.addEventListener('click', () => { mode = id; showMode(); previewed = null; put(recap); ({ percent, target, net }[id] ?? net).focus(); });
      return button;
    })) : null;
  function showMode() {
    for (const [id, row] of Object.entries(rows)) { row.hidden = id !== mode; if (row.style) row.style.display = id === mode ? '' : 'none'; }
    switcher?.querySelectorAll('[data-fs-listing-admin-mode]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.fsListingAdminMode === mode)));
  }
  showMode();
  keep?.addEventListener('change', () => { [percent, target, net, rate, hide].forEach((input) => { if (input) input.disabled = keep.checked; }); switcher?.querySelectorAll('button').forEach((button) => { button.disabled = keep.checked; }); put(recap); });
  const node = h('div', { class: 'fs-listing-admin-price', 'data-fs-listing-admin-price': true },
    h('dl', { class: 'fs-listing-admin-facts' },
      h('div', {}, h('dt', { text: 'Prix affiché' }), h('dd', { text: view.public || 'Pas encore de prix' })),
      ...(view.private || []).map(([label, value]) => h('div', {}, h('dt', { text: label }), h('dd', { text: value })))),
    view.pending ? h('p', { class: 'fs-alert', 'data-tone': 'info', role: 'status', text: view.pending }) : null,
    keep ? h('p', { class: 'fs-listing-admin-keep' }, keep, ' ', h('label', { for: 'fs-listing-admin-keep', text: 'Garder ce prix et lever l’attente (net et commission inchangés)' })) : null,
    locked ? h('p', { class: 'fs-alert', 'data-tone': 'warning', role: 'status', 'data-fs-listing-admin-price-lock': true, text: view.lock }) : null,
    switcher, percentRow, targetRow, netRow,
    hide ? h('p', { class: 'fs-listing-admin-keep', 'data-fs-listing-admin-hide-row': true }, hide, ' ', h('label', { for: 'fs-listing-admin-hide', text: 'Ne pas afficher comme une baisse (ni pastille ni prix barré sur le site)' })) : null,
    h('p', { class: 'fs-listing-admin-quote' }, quote), recap);
  const showErrors = (found = {}) => {
    for (const key of ['adminPercent', 'adminTarget', 'adminNet', 'adminRate']) {
      const field = node.querySelector(`[data-fs-field="${key}"]`);
      const line = field?.querySelector('[data-fs-error]');
      const message = found[key] ? `${found[key].charAt(0).toUpperCase()}${found[key].slice(1)}.` : '';
      if (line) { line.textContent = message; line.hidden = !message; }
      field?.classList.toggle('is-invalid', Boolean(message));
      field?.querySelector('input')?.setAttribute('aria-invalid', message ? 'true' : 'false');
    }
  };
  const intent = { current: null };
  return {
    node, errors, showErrors, change,
    summary() {
      const wanted = change();
      if (locked) return `${view.public || 'Sans prix'} · ${view.lock}`;
      if (!wanted || wanted.kind === 'none') return view.public ? `${view.public} (inchangé)` : 'Inchangé';
      if (wanted.kind === 'keep') return `${view.public || 'Prix en vigueur'} (gardé, attente levée)`;
      // Aperçu du service encore valable : sa répartition ; sinon la saisie, dite en clair (aucun montant calculé ici).
      if (previewed?.signature === signature()) return previewed.lines.summary;
      const value = inputs(), hidden = wanted.body?.showReduction === false ? ' · sans pastille' : '';
      if (wanted.kind === 'percent') return `Baisse de ${lib.formatRate(value.reductionBps)} : prix, net vendeur et commission au prorata${hidden}`;
      if (wanted.kind === 'target') return `Nouveau prix ${view.public ? `${view.public} → ` : ''}${lib.formatPrice(value.targetPriceMinor)} : net vendeur et commission au prorata${hidden}`;
      return wanted.kind === 'net' ? `Net ${base ? `${lib.formatPrice(base.netMinor)} → ` : ''}${lib.formatPrice(value.netMinor)} (sans accord du vendeur) · commission ${lib.formatRate(value.rateBps)}`
        : `Commission ${base ? `${lib.formatRate(base.rateBps)} → ` : ''}${lib.formatRate(value.rateBps)} · net inchangé`;
    },
    /** POST /admin/listings/:handle/price ; même identifiant tant que la demande ne change pas (rejeu sans doublon). */
    async save({ reason }) {
      const wanted = change();
      if (!wanted || wanted.kind === 'none' || wanted.kind === 'invalid') return null;
      return sendPrice(lib, { handle, call }, wanted, reason, intent);
    },
  };
}

/**
 * Écriture d'un changement de prix (`wanted` : { kind, body }) avec son motif, partagée par la fenêtre « Modifier l'annonce »
 * et le tiroir : POST /admin/listings/:handle/price. `intent.current` garde le même identifiant tant que la demande ne
 * change pas (rejeu sans doublon). → { ok, status, … } de priceResult, ou un refus { ok: false, status, message }.
 */
export async function sendPrice(lib, { handle, call }, wanted, reason, intent) {
  const sent = JSON.stringify([wanted.body, reason]);
  if (intent.current?.signature !== sent) intent.current = { signature: sent, requestId: rid('price') };
  const base = `/admin/listings/${encodeURIComponent(handle)}/price`, requestId = intent.current.requestId;
  let response;
  try { response = await call(base, { method: 'POST', body: { ...wanted.body, reason, requestId }, timeoutMs: PRICE_WRITE_MS }); }
  catch { response = { status: 0, body: null }; }
  // Réponse perdue ou incertaine (délai, coupure, 5xx) : l'état de la demande est relu d'abord ; il dit si le prix est
  // enregistré, refusé ou inconnu (inconnu : la même demande, rejouée, ne fait pas de doublon).
  if (response.status === 0 || response.status >= 500) {
    // Écriture du registre encore en cours (`writing`) : relue à intervalle croissant, jamais annoncée avant son issue.
    const seen = await readStatus(call, `${base}/status?${new URLSearchParams({ requestId })}`, { budgetMs: 20000, waitFirst: false,
      settled: (body) => body?.status !== 'writing' });
    const known = seen?.read ? priceResult(lib, { status: 200, body: seen.body }, { kind: wanted.kind, requestId }) : null;
    if (known) { if (known.ok || known.status < 500) intent.current = null; return known; }
  }
  const result = priceResult(lib, response, { kind: wanted.kind, requestId });
  if (result) { intent.current = null; return result; }
  if (response.status && response.status < 500) intent.current = null;
  return { ok: false, status: response.status, message: lib.priceErrorText(response.status, response.body) };
}

/* ------------------------------------------------------------------ prix, tiroir de la fiche */

/** Attente après la dernière saisie avant l'aperçu du service (ms) ; l'aperçu précédent, encore en route, est annulé. */
export const PREVIEW_MS = 350;
/** Écran « Changer le prix » du tiroir : textes de l'équipe. */
export const PRICE_SHEET = Object.freeze({
  title: 'Changer le prix.',
  lead: 'Appliqué dès l’enregistrement, sans accord du vendeur. Le service calcule le net, la commission et la pastille.',
  modes: Object.freeze([['percent', 'Baisser de %'], ['target', 'Nouveau prix'], ['net', 'Fixer le net']]),
  labels: Object.freeze({ percent: 'Baisse en pourcentage', target: 'Nouveau prix affiché, en euros', net: 'Net vendeur, en euros' }),
  help: Object.freeze({
    percent: 'Prix arrondi à l’euro inférieur ; net et commission baissent ensemble.',
    target: 'Euros entiers ; net et commission suivent au prorata, au même taux.',
    net: 'Net en euros entiers, sans accord du vendeur ; commission en %.',
    first: 'Premier prix : le net du vendeur et la commission font le prix affiché.',
  }),
  errors: Object.freeze({
    percent: 'Baisse : un pourcentage de 0,01 à 99, avec au plus deux décimales.',
    target: 'Nouveau prix : un nombre entier d’euros.',
    net: 'Net vendeur : un nombre entier d’euros, de 1 à 10 000.',
    rate: 'Commission : un pourcentage de 0 à 100, avec au plus deux décimales.',
  }),
  shortcuts: Object.freeze([500, 1000, 1500, 2000]),
  reasons: Object.freeze(['Demande du vendeur', 'Aligné sur le marché', 'Annonce silencieuse', 'Correction']),
  placeholder: 'Ou écrivez le motif',
  before: 'Aujourd’hui', after: 'Après', noPrice: 'Aucun',
  show: 'Montrer la baisse sur le site', showOn: 'Pastille et prix barré sur la fiche', showOff: 'Ni pastille ni prix barré ; une baisse affichée s’arrête',
  noBadge: 'Sans pastille',
  keep: 'Garder ce prix et lever l’attente',
  unchanged: 'Prix inchangé', apply: 'Appliquer le prix', keepApply: 'Garder ce prix',
  offline: 'Aperçu indisponible (service injoignable) : le prix sera calculé à l’enregistrement.',
  // Pourquoi aucune pastille (reductionNote) : l'interrupteur remplace la case « Ne pas afficher comme une baisse ».
  hidden: 'Baisse non montrée sur le site : aucune pastille, et toute baisse affichée de l’annonce s’arrête.',
});
/** Pastille de l'aperçu (`reduction` du service) → « −10 % · prix barré 144 € », '' sans baisse affichée. */
export function badgeText(lib, reduction, currency) {
  if (!Number.isSafeInteger(reduction?.percent) || reduction.percent <= 0 || !Number.isSafeInteger(reduction?.referenceMinor)) return '';
  return `−${reduction.percent}\u00a0% · prix barré ${lib.formatPrice(reduction.referenceMinor, currency)}`;
}
/**
 * Réponse de l'aperçu → vue normalisée { before, after, seller, commission, reduction, note } (montants du service), ou
 * null. `quote` : POST /admin/pricing/quote d'un premier prix (sans « avant »), `asked` : { netMinor, rateBps } envoyés.
 */
export function previewOf(body, { quote = false, asked = null } = {}) {
  if (quote) {
    if (!Number.isSafeInteger(body?.buyerTotalMinor) || !Number.isSafeInteger(body?.retainedMinor) || !Number.isSafeInteger(asked?.netMinor)) return null;
    return { before: null, after: { priceMinor: body.buyerTotalMinor, netMinor: asked.netMinor, rateBps: asked.rateBps, retainedMinor: body.retainedMinor },
      seller: null, commission: null, reduction: null, note: '' };
  }
  const after = body?.after;
  if (!Number.isSafeInteger(after?.priceMinor) || !Number.isSafeInteger(after?.netMinor) || !Number.isSafeInteger(after?.retainedMinor)) return null;
  return { before: body.before ?? null, after, seller: body.seller ?? null, commission: body.commission ?? null, reduction: body.reduction ?? null,
    note: typeof body.reductionNote === 'string' ? body.reductionNote : '' };
}
/**
 * État du bouton « Appliquer » d'après l'aperçu → { status, label, disabled } : 'unchanged' (rien saisi, ou le prix actuel),
 * 'invalid' (saisie illisible), 'pending' (aperçu en route : bouton gardé tel quel, inactif), 'ready', 'refused' (le
 * service refuserait), 'offline' (aperçu sans réponse : le service jugera à l'enregistrement).
 */
export function applyState(lib, status, { preview = null, previous = PRICE_SHEET.apply, keep = false, currency } = {}) {
  if (keep) return { status: 'ready', label: PRICE_SHEET.keepApply, disabled: false };
  if (status === 'unchanged') return { status, label: PRICE_SHEET.unchanged, disabled: true };
  if (status === 'pending') return { status, label: previous, disabled: true };
  if (status === 'ready' && Number.isSafeInteger(preview?.after?.priceMinor)) return { status, label: `Appliquer ${lib.formatPrice(preview.after.priceMinor, currency)}`, disabled: false };
  if (status === 'offline') return { status, label: PRICE_SHEET.apply, disabled: false };
  return { status, label: PRICE_SHEET.apply, disabled: true };
}

/**
 * Écran « Changer le prix » du tiroir (refonte du 29 octobre 2026) : avant → après mis à jour pendant la saisie par l'aperçu
 * du service (POST …/price/preview, ou /admin/pricing/quote pour un premier prix), PREVIEW_MS après la dernière frappe,
 * l'aperçu précédent annulé ; l'ancien reste à l'écran pendant le calcul. Trois façons (« Baisser de % », « Nouveau
 * prix », « Fixer le net » avec la commission), raccourcis de baisse, interrupteur « Montrer la baisse sur le site »
 * (inverse de showReduction: false). Aucun montant calculé ici : prix, net, commission, écarts, pastille et raisons viennent
 * du service ; la barre net / commission est dessinée par la mise en page (flex-grow = montants du service).
 * → { preview, controls, change(), state(), save({ reason }), destroy() }, ou null sans prix modifiable ici. `onChange(state)` :
 * état du bouton « Appliquer » (applyState).
 */
export function priceEditor(lib, current, { handle, call, onChange = () => {}, wait = PREVIEW_MS } = {}) {
  const view = lib.priceView(current, { publisher: true });
  const pricing = current?.pricing;
  if (!view.action || !pricing) return null;
  const currency = pricing.currency ?? current?.price?.currency;
  const money = (minor) => lib.formatPrice(minor, currency);
  const base = Number.isSafeInteger(pricing.netMinor) && Number.isSafeInteger(pricing.rateBps) ? { netMinor: pricing.netMinor, rateBps: pricing.rateBps } : null;
  const shownPrice = Number.isSafeInteger(current?.price?.minor) && current.price.source === 'listing' ? current.price.minor
    : Number.isSafeInteger(pricing.buyerTotalMinor) ? pricing.buyerTotalMinor : null;
  // Répartition en vigueur, lue telle quelle (accord du registre) : point de départ de l'aperçu.
  const baseline = base && Number.isSafeInteger(pricing.retainedMinor) && Number.isSafeInteger(shownPrice)
    ? { priceMinor: shownPrice, netMinor: pricing.netMinor, rateBps: pricing.rateBps, retainedMinor: pricing.retainedMinor } : null;
  const keepBody = view.lock ? null : lib.keepRequest(pricing);
  let mode = base ? 'percent' : 'net', show = true, keep = false;
  const values = { percent: '', target: '', net: lib.netField(pricing.netMinor) };
  const id = `fs-td-amount-${handle}`;
  const amount = h('input', { class: 'fs-td-amount-input', id, type: 'text', inputmode: 'decimal', autocomplete: 'off', 'aria-describedby': `${id}-help`, 'data-fs-listing-admin-amount': true });
  const amountLabel = h('label', { class: 'fs-visually-hidden', for: id });
  const unit = h('span', { class: 'fs-td-amount-unit', 'aria-hidden': 'true' });
  const rate = h('input', { class: 'fs-td-amount-input', id: `${id}-rate`, type: 'text', inputmode: 'decimal', autocomplete: 'off', value: lib.rateInput(pricing.rateBps ?? pricing.defaultRateBps ?? 2000),
    'aria-describedby': `${id}-help`, 'data-fs-listing-admin-rate': true });
  const rateBox = h('div', { class: 'fs-td-amount fs-td-amount--rate' }, h('label', { class: 'fs-visually-hidden', for: rate.id, text: 'Commission, en pourcentage' }), rate,
    h('span', { class: 'fs-td-amount-unit', 'aria-hidden': 'true', text: '%' }));
  const help = h('p', { class: 'fs-td-help', id: `${id}-help`, 'aria-live': 'polite' });
  const segments = segmented({ label: 'Façon de changer le prix', attrs: { class: 'fs-td-modes', 'data-fs-listing-admin-modes': true } });
  if (base) add(segments, ...PRICE_SHEET.modes.map(([key, label]) => segment(label, { pressed: key === mode, attrs: { 'data-fs-listing-admin-mode': key }, onClick: () => setMode(key) })));
  const shortcuts = h('div', { class: 'fs-td-chips', role: 'group', 'aria-label': 'Raccourcis de baisse' },
    PRICE_SHEET.shortcuts.map((bps) => chip(`−${lib.formatRate(bps)}`, { pressed: false, attrs: { 'data-fs-team-shortcut': String(bps) },
      onClick: () => { values.percent = lib.rateInput(bps); amount.value = values.percent; refresh(); } })));
  const showSub = h('span', { class: 'fs-td-toggle-sub' });
  const showSwitch = base ? h('button', { type: 'button', class: 'fs-td-toggle', role: 'switch', 'aria-checked': 'true', 'data-fs-listing-admin-show': true },
    h('span', { class: 'fs-td-toggle-text' }, h('span', { class: 'fs-td-toggle-label', text: PRICE_SHEET.show }), showSub), h('span', { class: 'fs-switch', 'aria-hidden': 'true' })) : null;
  showSwitch?.addEventListener('click', () => { show = !show; showSwitch.setAttribute('aria-checked', String(show)); refresh(); });
  const keepSwitch = keepBody ? h('button', { type: 'button', class: 'fs-td-toggle', role: 'switch', 'aria-checked': 'false', 'data-fs-listing-admin-keep': true },
    h('span', { class: 'fs-td-toggle-text' }, h('span', { class: 'fs-td-toggle-label', text: PRICE_SHEET.keep }), h('span', { class: 'fs-td-toggle-sub', text: view.pending })),
    h('span', { class: 'fs-switch', 'aria-hidden': 'true' })) : null;
  keepSwitch?.addEventListener('click', () => {
    keep = !keep;
    keepSwitch.setAttribute('aria-checked', String(keep));
    for (const node of [amount, rate, ...segments.querySelectorAll('button'), ...shortcuts.querySelectorAll('button')]) node.disabled = keep;
    refresh();
  });
  const fields = h('div', { class: 'fs-td-amounts' }, h('div', { class: 'fs-td-amount' }, amountLabel, amount, unit), rateBox);
  const controls = h('div', { class: 'fs-td-price-controls', 'data-fs-listing-admin-price': true }, base ? segments : null, fields, shortcuts, help, showSwitch, keepSwitch);

  /* Avant → après : lignes partagées (étiquettes, prix sur une même ligne de base, pastille à hauteur réservée). */
  const figure = (text) => { const { figure: digits, unit: sign } = lib.splitAmount(text); return [h('span', { text: digits }), sign ? h('span', { class: 'fs-td-unit', text: sign }) : null]; };
  const old = h('span', { class: 'fs-td-ba-old' }), next = h('span', { class: 'fs-td-ba-new', 'data-fs-team-preview-price': true });
  const badge = h('span', { class: 'fs-td-ba-badge' });
  const barNet = h('i', { class: 'fs-td-bar-net' }), barCut = h('i', { class: 'fs-td-bar-cut' });
  const legendNet = h('b'), legendCut = h('b'), legendRate = h('span');
  const deltaNet = h('b'), deltaCut = h('b');
  const note = h('p', { class: 'fs-td-ba-note' });
  const live = h('p', { class: 'fs-visually-hidden', 'aria-live': 'polite', 'data-fs-team-preview-live': true });
  const block = h('div', { class: 'fs-td-ba', 'data-fs-team-preview': true },
    h('span', { class: 'fs-td-ba-lab fs-td-ba-lab--old', text: PRICE_SHEET.before }), h('span', { class: 'fs-td-ba-lab fs-td-ba-lab--new', text: PRICE_SHEET.after }),
    old, h('span', { class: 'fs-td-ba-arrow', 'aria-hidden': 'true', text: '→' }), next, h('span', { class: 'fs-td-ba-slot' }, badge),
    h('div', { class: 'fs-td-split' }, h('div', { class: 'fs-td-bar', 'aria-hidden': 'true' }, barNet, barCut),
      h('p', { class: 'fs-td-legend' }, h('span', {}, h('span', { class: 'fs-td-key', 'aria-hidden': 'true' }), 'Net ', legendNet),
        h('span', {}, h('span', { class: 'fs-td-key fs-td-key--cut', 'aria-hidden': 'true' }), 'Commission ', legendRate, legendCut))),
    base ? h('p', { class: 'fs-td-ba-delta' }, h('span', {}, 'Vendeur ', deltaNet), h('span', {}, 'Commission ', deltaCut)) : null, note, live);

  // `displayed` : aperçu à l'écran ; gardé pendant le calcul suivant (pas de saut), retiré quand la saisie ne donne plus de prix.
  let status = 'unchanged', preview = null, displayed = null, message = '', tone = '', seq = 0, timer = null, controller = null;
  let button = applyState(lib, 'unchanged');
  const inputs = () => ({ reductionBps: lib.parseReduction(values.percent), targetPriceMinor: lib.parseTargetPrice(values.target),
    netMinor: lib.readNet(values.net, base?.netMinor), rateBps: lib.parseRate(rate.value) });
  /** Demande de la saisie en cours : { kind, body } (priceRequest), { kind: 'none' }, ou { kind: 'invalid', error }. */
  function change() {
    if (view.lock) return null;
    if (keep) return { kind: 'keep', body: keepBody, label: 'Prix (gardé, attente levée)' };
    const raw = values[mode].trim();
    if (!raw && (mode !== 'net' || !base)) return { kind: 'none', body: {} };
    const value = inputs();
    if (mode === 'percent' && value.reductionBps === null) return { kind: 'invalid', body: {}, error: PRICE_SHEET.errors.percent };
    if (mode === 'target' && value.targetPriceMinor === null) return { kind: 'invalid', body: {}, error: PRICE_SHEET.errors.target };
    if (mode === 'net' && value.netMinor === null) return { kind: 'invalid', body: {}, error: PRICE_SHEET.errors.net };
    if (mode === 'net' && value.rateBps === null) return { kind: 'invalid', body: {}, error: PRICE_SHEET.errors.rate };
    return lib.priceRequest(mode, value, base, { hide: Boolean(base) && !show });
  }
  function setMode(key) {
    values[mode] = amount.value;
    mode = key;
    amount.value = values[key];
    segments.querySelectorAll('[data-fs-listing-admin-mode]').forEach((node) => node.setAttribute('aria-pressed', String(node.dataset.fsListingAdminMode === key)));
    refresh();
    amount.focus();
  }
  function paint() {
    const kind = base ? mode : 'first';
    amountLabel.textContent = PRICE_SHEET.labels[mode];
    put(unit, mode === 'percent' ? '%' : '€');
    amount.placeholder = mode === 'percent' ? '10' : mode === 'target' ? lib.netInput(shownPrice) : '';
    rateBox.hidden = mode !== 'net';
    fields.dataset.mode = mode;
    shortcuts.hidden = mode !== 'percent';
    shortcuts.querySelectorAll('[data-fs-team-shortcut]').forEach((node) => node.setAttribute('aria-pressed', String(lib.parseReduction(values.percent) === Number(node.dataset.fsTeamShortcut))));
    const wanted = change();
    help.dataset.tone = wanted?.kind === 'invalid' ? 'danger' : '';
    put(help, wanted?.kind === 'invalid' ? wanted.error : PRICE_SHEET.help[kind]);
    const wrong = wanted?.kind !== 'invalid' ? '' : wanted.error === PRICE_SHEET.errors.rate ? 'rate' : 'amount';
    amount.setAttribute('aria-invalid', String(wrong === 'amount'));
    rate.setAttribute('aria-invalid', String(wrong === 'rate'));
    // Avant → après : l'aperçu du service, gardé à l'écran pendant le calcul suivant ; sans aperçu, la répartition en vigueur.
    const shown = displayed ? displayed.after : baseline;
    const changed = Boolean(shown && baseline && shown !== baseline && shown.priceMinor !== baseline.priceMinor);
    put(old, ...(Number.isSafeInteger(shownPrice) ? figure(money(shownPrice)) : [PRICE_SHEET.noPrice]));
    put(next, ...(shown ? figure(money(shown.priceMinor)) : ['—']));
    block.dataset.changed = String(changed);
    block.dataset.state = status;
    barNet.style.flexGrow = String(shown?.netMinor ?? 1);
    barCut.style.flexGrow = String(shown?.retainedMinor ?? 0);
    put(legendNet, shown ? money(shown.netMinor) : '—');
    put(legendCut, shown ? money(shown.retainedMinor) : '—');
    put(legendRate, shown && lib.formatRate(shown.rateBps) ? `${lib.formatRate(shown.rateBps)} ` : '');
    const current = displayed;
    put(deltaNet, lib.signedPrice(current?.seller?.deltaMinor ?? 0, currency));
    put(deltaCut, lib.signedPrice(current?.commission?.deltaMinor ?? 0, currency));
    const pill = current ? badgeText(lib, current.reduction, currency) : '';
    badge.dataset.on = String(Boolean(pill));
    put(badge, pill || (current ? PRICE_SHEET.noBadge : ''));
    const reason = current && !pill ? (current.note === 'hidden' ? PRICE_SHEET.hidden : lib.REDUCTION_NOTES[current.note] ?? '') : '';
    note.dataset.tone = message ? tone : '';
    put(note, message || reason);
    const until = current?.reduction ? lib.reductionUntil(current.reduction.until) : '';
    put(showSub, !show ? PRICE_SHEET.showOff : pill ? `Pastille ${pill}${until ? ` jusqu’au ${until}` : ''}` : PRICE_SHEET.showOn);
  }
  function announce() {
    if (status === 'ready' && displayed) {
      const shown = displayed;
      const lines = base ? lib.previewLines({ before: shown.before, after: shown.after, seller: shown.seller, commission: shown.commission, reduction: shown.reduction, reductionNote: shown.note }, currency) : null;
      put(live, `Après : ${lines ? lines.summary : `${money(shown.after.priceMinor)} · commission ${money(shown.after.retainedMinor)}`}`);
    } else if (message) put(live, message);
  }
  function settle(next, { body = null, text = '', level = '' } = {}) {
    status = next;
    if (body) preview = body;
    if (next === 'ready') displayed = keep ? null : preview;
    else if (next !== 'pending') displayed = null;
    message = text;
    tone = level;
    button = applyState(lib, status, { preview, previous: button.label, keep, currency });
    paint();
    announce();
    onChange(button);
  }
  /** Saisie changée : aperçu annulé, puis redemandé PREVIEW_MS après la dernière frappe. */
  function refresh() {
    clearTimeout(timer);
    controller?.abort();
    seq++;
    const wanted = change();
    if (!wanted) { settle('refused', { text: view.lock, level: 'warning' }); return; }
    if (wanted.kind === 'keep') { settle('ready'); return; }
    if (wanted.kind === 'none') { settle('unchanged'); return; }
    if (wanted.kind === 'invalid') { settle('invalid'); return; }
    settle('pending', { text: message, level: tone });
    const mine = seq;
    timer = setTimeout(() => run(wanted, mine), wait);
  }
  async function run(wanted, mine) {
    controller = typeof AbortController === 'function' ? new AbortController() : null;
    const asked = { netMinor: inputs().netMinor, rateBps: inputs().rateBps };
    let response;
    try {
      response = base ? await call(`/admin/listings/${encodeURIComponent(handle)}/price/preview`, { method: 'POST', body: wanted.body, signal: controller?.signal })
        : await call('/admin/pricing/quote', { method: 'POST', body: asked, signal: controller?.signal });
    } catch { response = { status: 0, body: null }; }
    if (mine !== seq) return;
    const body = response.status === 200 ? previewOf(response.body, { quote: !base, asked }) : null;
    if (body) { settle('ready', { body }); return; }
    if (response.body?.error === 'price_unchanged') { settle('unchanged'); return; }
    if (!response.status || response.status >= 500) { settle('offline', { text: PRICE_SHEET.offline, level: 'warning' }); return; }
    settle('refused', { text: lib.priceErrorText(response.status, response.body), level: 'danger' });
  }
  amount.addEventListener('input', () => { values[mode] = amount.value; refresh(); });
  rate.addEventListener('input', () => refresh());
  const intent = { current: null };
  settle('unchanged');
  if (view.lock) settle('refused', { text: view.lock, level: 'warning' });
  return {
    preview: block, controls, change, focus: () => amount.focus({ preventScroll: true }),
    title: view.action === 'Modifier le prix' ? PRICE_SHEET.title : `${view.action}.`, lead: base ? PRICE_SHEET.lead : PRICE_SHEET.help.first,
    reasons: PRICE_SHEET.reasons, placeholder: PRICE_SHEET.placeholder,
    state: () => ({ ...button }),
    async save({ reason }) {
      const wanted = change();
      if (!wanted || wanted.kind === 'none' || wanted.kind === 'invalid') return null;
      return sendPrice(lib, { handle, call }, wanted, reason, intent);
    },
    destroy() { clearTimeout(timer); controller?.abort(); seq++; },
  };
}

/* ------------------------------------------------------------------ fiche */

/** Feuille chargée une fois (lien <link> attendu) ; échec : la fenêtre reste lisible avec les styles de la page. */
const sheets = new Map();
function loadCss(href) {
  if (!href) return Promise.resolve();
  if (!sheets.has(href)) {
    sheets.set(href, new Promise((resolve) => {
      if ([...document.styleSheets].some((sheet) => sheet.href && sheet.href.split('?')[0] === new URL(href, location.href).href.split('?')[0])) { resolve(); return; }
      const link = h('link', { rel: 'stylesheet', href });
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      document.head.append(link);
    }));
  }
  return sheets.get(href);
}
/** Fiche de travail de l'annonce dans l'espace équipe (adresse dans la langue de la page). */
export const workspaceHref = (handle) => path(`pages/moderation?${new URLSearchParams({ ecran: 'catalogue', handle })}`);
/** Message gardé pour la fiche rechargée (tiroir rouvert), montré tout de suite, puis rechargement. */
function reloadWith(message, handle) {
  try { sessionStorage.setItem(FLASH_KEY, JSON.stringify({ handle, text: message.text, tone: message.tone, at: Date.now(), reopen: true })); } catch { /* message montré tout de suite */ }
  toast(message.text, { tone: message.tone, timeout: 0 });
  setTimeout(() => location.reload(), 900);
}
/* Prix de la fiche au format de la page, même règle que formatEuro (fs-purchase.js) et fs-listing-price : décimales seulement
   s'il y a des centimes, espaces insécables. Montant du service, jamais calculé ici. */
const pageEuro = (minor) => {
  const digits = minor % 100 ? 2 : 0;
  return number(minor / 100, { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits }).replace(/[\u202f ]/g, '\u00a0');
};
/** Nouveau prix montré sur la fiche ouverte (bloc d'achat), sans la recharger ; `dropped` : baisse affichée retirée. */
export function showPagePrice(root, minor, { dropped = false } = {}) {
  if (Number.isSafeInteger(minor) && minor > 0) {
    for (const node of root.querySelectorAll('.fs-ld-price [data-fs-price]')) { node.textContent = pageEuro(minor); node.dataset.fsPrice = String(minor); }
  }
  if (dropped) for (const node of root.querySelectorAll('.fs-ld-price .fs-ld-was')) node.hidden = true;
}
/** Écusson de la pastille (trait, couleur de l'accent du jeu), décoratif. */
function shield() {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  for (const [key, value] of Object.entries({ viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false', class: 'fs-team-pill-icon' })) svg.setAttribute(key, value);
  const line = document.createElementNS(ns, 'path');
  line.setAttribute('d', 'M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z');
  svg.append(line);
  return svg;
}
/** Copie décorative d'une image ou d'un emblème de la fiche (sans identifiant, classe ni nom lu), ou null. */
function decorative(node) {
  if (!node) return null;
  const copy = node.cloneNode(true);
  for (const name of ['id', 'class', 'data-fs-vt-morph', 'data-fs-gp-logo', 'data-fs-gp-on']) copy.removeAttribute(name);
  copy.setAttribute('aria-hidden', 'true');
  if (copy.tagName === 'IMG') { copy.alt = ''; copy.loading = 'eager'; copy.removeAttribute('sizes'); }
  return copy;
}
const assetsOf = (node) => { try { const value = JSON.parse(node.dataset.fsAssets || '{}'); return value && typeof value === 'object' ? value : {}; } catch { return {}; } };

/** Bloc du parcours de création, tel que le rend le snippet fs-propose (sans le choix des jeux : celui de l'annonce). */
function proposeRoot({ service, assets, game, gameName }) {
  return h('div', { class: 'fs-propose fs-propose--steps fs-listing-edit-flow', 'data-fs-propose': true, 'data-fs-service': service, 'data-fs-universe': game, 'data-fs-game-pack': game,
    'data-fs-templates': assets.templates, 'data-fs-listings-css': assets.listingsCss, 'data-fs-showcase-js': assets.showcaseJs, 'data-fs-showcase-css': assets.showcaseCss,
    'data-fs-import-ui': assets.importUi, 'data-fs-account-import': assets.accountImport, 'data-fs-media': assets.media, hidden: true },
  h('div', { class: 'fs-propose-console', 'data-fs-propose-console': true, hidden: true },
    h('span', { class: 'fs-propose-console-badge', 'data-fs-propose-badge': true, 'aria-hidden': 'true' }),
    h('div', { class: 'fs-propose-console-text' }, h('p', { class: 'fs-propose-console-game' }, h('span', { 'data-fs-propose-current': true }),
      h('span', { class: 'fs-propose-saved', 'data-fs-propose-saved': true, role: 'status', hidden: true }))),
    h('button', { type: 'button', class: 'fs-btn fs-btn--link fs-propose-change', 'data-fs-propose-change': true, hidden: true })),
  h('p', { class: 'fs-alert fs-propose-game-note', role: 'status', 'data-fs-propose-game-note': true, hidden: true }),
  h('nav', { class: 'fs-step-rail-wrap fs-rail', 'data-fs-propose-rail': true, 'aria-label': 'Étapes de l’annonce', hidden: true }),
  h('div', { class: 'fs-propose-flow' },
    h('div', { class: 'fs-propose-banners', 'data-fs-propose-banners': true }),
    h('fieldset', { class: 'fs-propose-step fs-propose-step-game', 'data-fs-propose-chooser': true, hidden: true },
      h('legend', { class: 'fs-propose-step-title', text: gameName }),
      h('label', { class: 'fs-propose-game' }, h('input', { type: 'radio', name: 'fs-listing-admin-game', value: game, 'data-fs-propose-game': true }),
        h('span', { class: 'fs-propose-game-name', text: gameName }))),
    h('p', { class: 'fs-alert fs-propose-alert', 'data-tone': 'danger', role: 'alert', tabindex: '-1', 'data-fs-propose-auth-error': true, hidden: true }),
    h('p', { class: 'fs-propose-status', role: 'status', 'data-fs-propose-form-status': true, hidden: true }),
    h('div', { 'data-fs-propose-form-slot': true }),
    h('p', { class: 'fs-propose-credits', 'data-fs-propose-credits': true, hidden: true })));
}

function initialise(slot) {
  if (slot.dataset.fsListingAdminReady) return;
  const account = window.FSAccount;
  const handle = slot.dataset.fsHandle || '';
  const game = slot.dataset.fsGame || '';
  if (!account || !HANDLE.test(handle) || !GAME.test(game)) return;
  slot.dataset.fsListingAdminReady = 'true';
  const reference = (slot.dataset.fsRef || '').slice(0, 40);
  const gameName = (slot.dataset.fsGameName || game).slice(0, 60);
  const assets = assetsOf(slot);
  let rights = null;
  /* Tiroir de l'équipe et lecture de l'annonce : génération de session (une réponse d'une ancienne session est jetée). */
  let generation = 0, drawer = null, pill = null, reopen = false;
  let data = { listing: null, content: null, at: 0 };
  /* Changement de session : tiroir fermé et données vidées aussitôt, avant toute relecture de /me (revue croisée Codex). */
  const dropTeam = () => { generation++; drawer?.close(); drawer = null; data = { listing: null, content: null, at: 0 }; };
  let open = null;
  /* Fenêtre ouverte : compte et mode à l'ouverture. Autre compte, déconnexion ou droits perdus : fermée et vidée (net,
     commission, saisie), les lectures en cours s'arrêtent (revue croisée Codex du 12 octobre 2026). */
  let openUser = '';
  let openMode = '';

  /*
   * Parcours de création (fs-propose.js, 380 Ko, ses trois feuilles, puis ses modèles et l'import qu'il attend à
   * l'ouverture). Seule l'équipe voit « Modifier » : dès la barre montrée, pendant les temps morts, les modules sont
   * préchargés (téléchargés et compilés, pas exécutés) et les feuilles mises en cache, sans être appliquées. Au clic,
   * tout part en même temps que la lecture de l'annonce.
   */
  let warmed = false;
  function warmEditor() {
    if (warmed) return;
    warmed = true;
    const idle = window.requestIdleCallback ?? ((fn) => setTimeout(fn, 1500));
    idle(() => {
      for (const href of [assets.propose, assets.templates, assets.importUi]) if (href) document.head.append(h('link', { rel: 'modulepreload', href }));
      for (const href of [assets.proposeCss, assets.proposeStepsCss, assets.proposeFormCss]) if (href) document.head.append(h('link', { rel: 'prefetch', as: 'style', href }));
    });
  }
  let flowLoading = null;
  function loadFlow() {
    flowLoading ??= Promise.all([import(assets.propose), ...[assets.proposeCss, assets.proposeStepsCss, assets.proposeFormCss].map(loadCss)])
      .then(([module]) => module, (error) => { flowLoading = null; throw error; });
    /* Sans effet au chargement : fs-propose les reprend (même adresse, même module) au lieu de les demander après coup. */
    for (const href of [assets.templates, assets.importUi]) if (href) import(href).catch(() => {});
    return flowLoading;
  }

  /* Module du tiroir (équipe seulement) : chargé dès la pastille montrée, pour son chiffre et une ouverture sans attente. */
  let drawerLoading = null;
  const loadDrawer = () => {
    drawerLoading ??= assets.drawer ? import(assets.drawer).catch(() => { drawerLoading = null; return null; }) : Promise.resolve(null);
    return drawerLoading;
  };
  const pageBar = () => document.querySelector('[data-fs-listing-page] [data-fs-ld] .fs-ld-bar-in');
  const pageTitle = () => (document.querySelector('[data-fs-listing-page] [data-fs-ld] #fs-detail-title')?.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 140);
  /**
   * Identité de l'annonce lue sur la fiche, pour le bandeau du tiroir : vaisseau de la baie (ou emblème, ou affiche), emblème
   * du jeu, serveur. Copies décoratives (aria-hidden, sans identifiant) ; page « plus disponible » : emblème seul.
   */
  const pageIdentity = () => {
    const root = document.querySelector('[data-fs-listing-page]');
    const art = root?.querySelector('[data-fs-ld] .fs-ld-bay [data-fs-bay-ship] .fs-bay-hero, [data-fs-ld] .fs-ld-bay .fs-bay-emblem > svg, [data-fs-ld] .fs-ld-bay .fs-bay-poster');
    const logo = root?.querySelector('[data-fs-ld] .fs-ld-meta-mark img, .fs-listing-page-gone-mark img');
    const meta = root?.querySelector('[data-fs-ld] .fs-ld-meta-game')?.cloneNode(true);
    meta?.querySelectorAll('.fs-visually-hidden, .fs-ld-game-name, .fs-ld-meta-sep').forEach((node) => node.remove());
    return { art: decorative(art), logo: decorative(logo), server: (meta?.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 40) };
  };

  /** Pastille « Équipe » : dans la barre de la fiche, sinon (page « plus disponible ») dans l'emplacement de repli. */
  function mountPill() {
    if (!pill) {
      pill = h('button', { type: 'button', class: 'fs-team-pill', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', 'data-fs-listing-admin-pill': true },
        shield(), h('span', { class: 'fs-team-pill-label', text: 'Équipe' }), h('span', { class: 'fs-team-pill-count', 'data-fs-listing-admin-count': true, hidden: true }));
      pill.addEventListener('click', () => openTools());
    }
    const bar = pageBar();
    if (bar) {
      if (pill.parentNode !== bar) bar.insertBefore(pill, bar.querySelector('.fs-ld-bar-ref, .fs-ld-bar-share'));
      put(slot); slot.hidden = true;
    } else {
      if (pill.parentNode?.parentNode !== slot) put(slot, h('div', { class: 'fs-team-slot' }, pill));
      slot.hidden = false;
    }
  }
  function removePill() { pill?.remove(); pill = null; put(slot); slot.hidden = true; }
  /** Chiffre de la pastille : points « À traiter » (sans la liste des personnes, lue à l'ouverture). */
  async function updatePill() {
    const module = await loadDrawer();
    const count = pill && module && rights?.team ? module.pillCount(data, rights) : 0;
    const node = pill?.querySelector('[data-fs-listing-admin-count]');
    if (!node) return;
    node.textContent = count ? String(count) : '';
    node.hidden = !count;
    pill.setAttribute('aria-label', count ? `Outils de l’équipe, ${count} point${count > 1 ? 's' : ''} à traiter` : 'Outils de l’équipe');
  }
  /** Annonce et contenu relus (équipe) : GET /admin/listings/:handle et …/content, ensemble ; une réponse dépassée est jetée. */
  async function refresh(mine = generation) {
    const read = (suffix) => account.call(`/admin/listings/${encodeURIComponent(handle)}${suffix}`).catch(() => ({ status: 0, body: null }));
    const [listing, content] = await Promise.all([read(''), read('/content')]);
    if (mine !== generation) return data;
    data = { listing: listing.status === 200 && listing.body?.listing && typeof listing.body.listing === 'object' ? listing.body.listing : null,
      content: content.status === 200 && content.body && typeof content.body === 'object' ? content.body : null, at: Date.now(), propagation: data?.propagation ?? null };
    updatePill();
    return data;
  }
  /** Issue d'une action du tiroir : rechargement de la fiche (tiroir rouvert ensuite) ou message sur place. */
  function finish(result) {
    const message = result.price ? savedMessage({ price: result.price }) : { text: result.text, tone: result.tone, reload: result.reload };
    if (message.reload) { reloadWith(message, handle); return; }
    toast(message.text, { tone: message.tone, dismissible: true, timeout: 12000 });
    if (result.price?.propagation?.status === 'pending') followPrice(result.price);
  }
  /**
   * Prix enregistré, fiche, offre et sujet Discord encore en cours (réponse avant la propagation) : nouveau prix montré sur
   * la page, prix non modifiable dans le tiroir, état relu (…/price/status) jusqu'à confirmation, puis tiroir relu sur place.
   * Jamais de rechargement de la fiche.
   */
  async function followPrice(price, mine = generation) {
    const requestId = price.propagation.requestId;
    showPagePrice(document, price.priceMinor);
    data = { ...data, propagation: { status: 'pending', requestId } };
    drawer?.rerender();
    // Registre déjà à jour : net et commission relus tout de suite (le service calcule, jamais le navigateur).
    refresh(mine).then(() => { if (mine === generation) drawer?.rerender(); });
    const seen = await readStatus(account.call, `/admin/listings/${encodeURIComponent(handle)}/price/status?${new URLSearchParams({ requestId })}`,
      { current: () => mine === generation });
    if (!seen || mine !== generation) return;
    data = { ...data, propagation: null };
    const read = seen.body;
    if (read?.status === 'done') {
      showPagePrice(document, read.price?.minor, { dropped: read.reduction === null });
      toast('Prix à jour sur la fiche, l’offre d’achat et Discord.', { tone: 'success', dismissible: true, timeout: 8000 });
    } else if (read?.status === 'failed') toast(`Nouveau prix enregistré, ${PROPAGATION_FAILED}`, { tone: 'warning', dismissible: true, timeout: 0 });
    // Dernière lecture sans réponse (service injoignable ou occupé) : dit tel quel, jamais pris pour « encore en cours ».
    else if (!seen.read) toast('État de la mise à jour non relu (service injoignable ou occupé) : rechargez la fiche dans une minute pour vérifier.', { tone: 'warning', dismissible: true, timeout: 12000 });
    else toast('Mise à jour encore en cours : rechargez la fiche dans une minute pour la voir.', { tone: 'info', dismissible: true, timeout: 12000 });
    await refresh(mine);
    if (mine === generation) drawer?.rerender();
  }

  /** Tiroir de l'équipe (fs-listing-drawer.js) ouvert depuis la pastille ; données de plus d'une minute relues. */
  async function openTools() {
    if (!rights?.team || drawer || open) return;
    const mine = generation;
    const module = await loadDrawer();
    if (!module || mine !== generation || !rights?.team || drawer || !pill) return;
    pill.setAttribute('aria-expanded', 'true');
    drawer = module.openDrawer({ account, handle, reference, gameName, title: data.listing?.title || pageTitle(), opener: pill,
      rights: () => rights, data: () => data, refresh: () => refresh(mine), current: () => mine === generation,
      identity: pageIdentity(), openEditor: (opener) => openEditor(opener ?? pill),
      priceEditor: (lib, listing, onChange) => priceEditor(lib, listing, { handle, call: account.call, onChange }),
      reasonError: adminReasonError, finish,
      onClosed: () => { drawer = null; pill?.setAttribute('aria-expanded', 'false'); } });
    if (Date.now() - data.at > 60_000) refresh(mine).then(() => drawer?.rerender());
  }

  /* Pastille de l'équipe : montrée seulement avec une session dont /me dit « équipe » (le service rejuge chaque appel). */
  async function render() {
    const mine = ++generation;
    // Droits gardés localement : une ancienne lecture de /me ne remplace jamais ceux d'une session plus récente.
    const read = await barRights({ me: () => (account.session() ? account.me() : null), css: () => loadCss(assets.adminCss), current: () => mine === generation });
    if (read.stale || !slot.isConnected) return;
    rights = read.rights;
    if (!rights?.team) { removePill(); return; }
    mountPill();
    warmEditor();
    await refresh(mine);
    if (mine !== generation) return;
    if (reopen) { reopen = false; openTools(); }
  }

  /** Fenêtre « Modifier l'annonce » : lecture, puis parcours de création (site) ou contenu public (Discord). */
  async function openEditor(opener) {
    if (open) return;
    let editor = null;
    let busy = false;
    const state = { dirty: () => false };
    saving = () => busy || Boolean(editor?.busy?.());
    const box = dialog({
      title: 'Modifier l’annonce', eyebrow: `Équipe${reference ? ` · ${reference}` : ''}`, size: 'xl', pinned: true, fullMobile: true, close: 'Fermer',
      className: 'fs-listing-edit-dialog', attrs: { 'data-fs-listing-edit': handle }, body: [skeleton({ rows: 4, label: 'Lecture de l’annonce' })],
      canClose: () => !busy && !editor?.busy?.(),
      onDismiss: async () => {
        if (busy || editor?.busy?.()) return;
        if (state.dirty() && !(await confirmDialog({ title: 'Abandonner les modifications ?', text: 'Rien n’a été enregistré : les changements de cette fenêtre seront perdus.',
          confirmLabel: 'Abandonner', cancelLabel: 'Continuer la modification', tone: 'danger' }))) return;
        box.close('cancel');
      },
      onClose: () => { open = null; },
    });
    open = box;
    openUser = sessionSub(account.session()?.token);
    openMode = '';
    box.open({ opener });
    const done = (result) => {
      const message = savedMessage(result);
      box.close('saved');
      if (message.reload) { reloadWith(message, handle); return; }
      toast(message.text, { tone: message.tone, dismissible: true, timeout: 12000 });
      if (result?.price?.propagation?.status === 'pending') followPrice(result.price);
    };
    const flow = loadFlow();
    flow.catch(() => {});
    let response;
    try { response = await account.call(`/admin/listings/${encodeURIComponent(handle)}/edit`); } catch { response = { status: 0, body: null }; }
    if (!box.node.open) return;
    if (response.status !== 200) { put(box.body, alert({ tone: 'danger', text: loadErrorText(response.status, response.body) })); return; }
    const view = editView(response.body);
    openMode = view.mode;
    if (view.kind === 'blocked') {
      put(box.body, alert({ tone: 'warning', text: view.text, attrs: { 'data-fs-listing-edit-blocked': response.body?.reason || 'blocked' } }),
        h('p', {}, h('a', { class: 'fs-link', href: workspaceHref(handle), text: 'Ouvrir l’annonce dans l’espace équipe' })));
      return;
    }
    const pricing = view.mode === 'direct' && rights?.publisher && rights?.prices ? loadPrice() : Promise.resolve(null);
    if (view.kind === 'site') {
      let propose;
      try {
        propose = await flow;
      } catch { if (box.node.open) put(box.body, alert({ tone: 'danger', text: 'Le formulaire n’a pas pu être chargé : réessayez dans un instant.' })); return; }
      const price = await pricing;
      if (!box.node.open) return;
      const root = proposeRoot({ service: account.service, assets, game: response.body.game, gameName });
      put(box.body, root);
      /* Reprise d'une annonce Discord : bandeau propre, captures non reprises (le modèle Discord ne les porte pas). */
      editor = propose.mountListingEditor(root, { handle, mode: view.mode, reference, scroller: box.body, onSaved: done, onBusy: (value) => { busy = value; },
        text: ADMIN_TEXT, labels: ADMIN_LABELS, errorText: adminErrorText, reasonError: adminReasonError,
        ...(view.takeover ? { variant: view.takeover === 'new' ? 'takeoverNew' : 'takeover', captures: false, note: takeoverNote(response.body.prefill) } : {}) });
      if (!editor) { put(box.body, alert({ tone: 'danger', text: 'Le formulaire n’a pas pu être ouvert.' })); return; }
      state.dirty = () => editor.dirty();
      await editor.load(response.body, price);
      return;
    }
  }

  /** Bloc du prix (publication, prix actifs) : lecture de l'annonce de l'espace équipe et des fonctions de fs-admin-price.js. */
  async function loadPrice() {
    try {
      const [response, lib] = await Promise.all([account.call(`/admin/listings/${encodeURIComponent(handle)}`), import('./fs-admin-price.js')]);
      return response.status === 200 && response.body?.listing ? priceBlock(lib, response.body.listing, { handle, call: account.call }) : null;
    } catch { return null; }
  }

  /* Message gardé avant le rechargement qui suit un enregistrement ; le tiroir se rouvre ensuite là où il était. */
  try {
    const flash = JSON.parse(sessionStorage.getItem(FLASH_KEY) || 'null');
    sessionStorage.removeItem(FLASH_KEY);
    if (flash?.handle === handle && Date.now() - flash.at < 60_000 && typeof flash.text === 'string') {
      toast(`${flash.text.slice(0, 400)} Si la fiche n’affiche pas encore les changements, rechargez-la dans quelques secondes.`, { tone: flash.tone, dismissible: true, timeout: 12000 });
      reopen = flash.reopen === true;
    }
  } catch { /* stockage indisponible */ }
  let saving = () => false;
  const closeOpen = () => {
    if (!open) return;
    const box = open, during = saving();
    open = null;
    box.close('account');
    toast(during ? 'Session changée : la fenêtre de modification est fermée. Un enregistrement était en cours : rechargez la fiche pour voir s’il a abouti.'
      : 'Session changée : la fenêtre de modification est fermée, rien n’a été enregistré.', { tone: 'warning', dismissible: true, timeout: 12000 });
  };
  document.addEventListener('fs:account', async () => {
    /* Tiroir fermé et données vidées tout de suite, avant la relecture de /me (contre-vérification Codex) : un compte qui
       n'est plus de l'équipe ne voit jamais ni l'état ni les personnes, même pendant l'attente. */
    dropTeam();
    /* Déconnexion ou autre compte : fermée tout de suite, avant toute lecture réseau (contre-vérification Codex). Cet écouteur
       est posé avant celui du parcours (ouvert plus tard) : le parcours, retiré de la page, n'adopte jamais la session. */
    const session = account.session();
    if (open && (!session || sessionSub(session.token) !== openUser)) closeOpen();
    await render();
    // Même compte, droits relus : plus d'équipe, ou plus de publication pour une fenêtre en mode direct.
    if (open && (!rights?.team || (openMode === 'direct' && !rights.publisher))) closeOpen();
  });
  render();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-fs-listing-admin]').forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll?.('[data-fs-listing-admin]').forEach(initialise));
}
